import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Upload } from "lucide-react";

const CATS = ["Model", "Actor", "Actress", "Model & Actor", "Influencer", "Dancer", "Singer", "Creator", "Kid Artist", "Fresh Face", "Other"];
const GENDERS = ["", "female", "male", "non-binary"];

const BLANK = {
  full_name: "", professional_name: "", slug: "", gender: "female", category: "Model", city: "", country: "India",
  age: "", height: "", chest: "", waist: "", hips: "", shoe_size: "", hair: "", eyes: "",
  bio: "", skills: "", languages: "",
  instagram: "", youtube: "", portfolio_url: "",
  profile_photo: "", photos: [], video_links: [],
  status: "verified", featured: false,
};

export default function AdminQuickAddModel({ onCreated }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [vTitle, setVTitle] = useState("");
  const [vUrl, setVUrl] = useState("");

  const set = (k, v) => setF({ ...f, [k]: v });

  const upload = (multi = false) => async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setBusy(true);
    try {
      const results = await Promise.all(files.map(async (fl) => {
        const fd = new FormData();
        fd.append("file", fl);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return data.url;
      }));
      if (multi) setF((x) => ({ ...x, photos: [...(x.photos || []), ...results].slice(0, 20) }));
      else setF((x) => ({ ...x, profile_photo: results[0] }));
      toast.success(`${results.length} photo${results.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const addVideo = () => {
    if (!vUrl) return;
    set("video_links", [...(f.video_links || []), { title: vTitle || "Video", url: vUrl, category: "Music Video" }]);
    setVTitle(""); setVUrl("");
  };

  const rmVideo = (i) => set("video_links", f.video_links.filter((_, x) => x !== i));
  const rmPhoto = (i) => set("photos", f.photos.filter((_, x) => x !== i));

  const save = async () => {
    if (!f.full_name || !f.city) { toast.error("Name and City are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/talents/create", f);
      toast.success(`Model created (${f.status}). ${f.status === "verified" ? "Live on /talent" : ""}`);
      setF(BLANK); onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  return (
    <div className="sv-card p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="sv-overline mb-1">Quick Add Model</div>
          <div className="font-serif-editorial text-xl text-[#F4EFE4]">Add a new model to the directory</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <input required placeholder="Full Name *" className="sv-input" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="qa-name" />
        <input placeholder="Professional Name" className="sv-input" value={f.professional_name} onChange={(e) => set("professional_name", e.target.value)} />
        <input placeholder="URL slug (auto if empty, e.g. vishakha)" className="sv-input" value={f.slug} onChange={(e) => set("slug", e.target.value)} data-testid="qa-slug" />
        <select className="sv-select" value={f.gender} onChange={(e) => set("gender", e.target.value)} data-testid="qa-gender">
          <option value="female">Female → /women/…</option>
          <option value="male">Male → /men/…</option>
          <option value="non-binary">Non-binary</option>
          <option value="">Unspecified → /talent/…</option>
        </select>
        <select className="sv-select" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="qa-category">
          {CATS.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input required placeholder="City *" className="sv-input" value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="qa-city" />
        <input placeholder="Age" className="sv-input" value={f.age} onChange={(e) => set("age", e.target.value)} />
        <input placeholder='Height (e.g. 5&apos;8")' className="sv-input" value={f.height} onChange={(e) => set("height", e.target.value)} />
        {["chest", "waist", "hips"].map((k) => <input key={k} placeholder={k.toUpperCase()} className="sv-input" value={f[k]} onChange={(e) => set(k, e.target.value)} />)}
        {["shoe_size", "hair", "eyes"].map((k) => <input key={k} placeholder={k.replace("_", " ").toUpperCase()} className="sv-input" value={f[k]} onChange={(e) => set(k, e.target.value)} />)}
        <input placeholder="Skills (comma separated)" className="sv-input md:col-span-2" value={f.skills} onChange={(e) => set("skills", e.target.value)} />
        <input placeholder="Languages" className="sv-input" value={f.languages} onChange={(e) => set("languages", e.target.value)} />
        <input placeholder="Instagram @handle" className="sv-input" value={f.instagram} onChange={(e) => set("instagram", e.target.value)} />
        <input placeholder="YouTube channel URL" className="sv-input" value={f.youtube} onChange={(e) => set("youtube", e.target.value)} />
        <input placeholder="Portfolio URL" className="sv-input" value={f.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} />
        <textarea rows={3} placeholder="About / Bio" className="sv-input md:col-span-2" value={f.bio} onChange={(e) => set("bio", e.target.value)} />
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div>
          <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Profile Photo</div>
          <label className="block border border-dashed border-[rgba(197,160,89,0.35)] rounded-xl p-4 text-center cursor-pointer hover:border-[#C5A059] transition">
            <input type="file" accept="image/*" className="hidden" onChange={upload(false)} data-testid="qa-profile-photo" />
            {f.profile_photo ? <img src={f.profile_photo} className="mx-auto rounded-lg max-h-48 object-cover" alt="" /> : <div className="text-xs font-mono-label text-[#8A8478] py-8 flex flex-col items-center gap-2"><Upload size={18} />Upload Profile Photo</div>}
          </label>
        </div>
        <div>
          <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Portfolio Photos (4–5 HD recommended)</div>
          <label className="block border border-dashed border-[rgba(197,160,89,0.35)] rounded-xl p-4 text-center cursor-pointer hover:border-[#C5A059] transition">
            <input type="file" accept="image/*" multiple className="hidden" onChange={upload(true)} data-testid="qa-gallery" />
            <div className="text-xs font-mono-label text-[#8A8478] py-2 flex items-center justify-center gap-2"><Plus size={14} />Add HD Photos</div>
          </label>
          {f.photos.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {f.photos.map((g, i) => (
                <div key={i} className="relative group">
                  <img src={g} className="rounded aspect-square object-cover" alt="" />
                  <button onClick={() => rmPhoto(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#0A0A0B]/80 grid place-items-center opacity-0 group-hover:opacity-100 transition"><X size={12} className="text-[#F4EFE4]" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Video Links (YouTube / Instagram / Vimeo)</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr,2fr,auto] gap-2">
          <input placeholder="Title" className="sv-input" value={vTitle} onChange={(e) => setVTitle(e.target.value)} />
          <input placeholder="Video URL (https://…)" className="sv-input" value={vUrl} onChange={(e) => setVUrl(e.target.value)} data-testid="qa-video-url" />
          <button type="button" onClick={addVideo} className="sv-btn-outline text-xs inline-flex items-center gap-1"><Plus size={12} />Add</button>
        </div>
        {f.video_links.length > 0 && (
          <ul className="mt-3 space-y-2">
            {f.video_links.map((v, i) => (
              <li key={i} className="flex items-center justify-between text-xs text-[#CDC7BA] border border-[rgba(255,255,255,0.06)] rounded-lg px-3 py-2">
                <span className="truncate"><strong className="text-[#F4E4BC]">{v.title}</strong> — {v.url}</span>
                <button onClick={() => rmVideo(i)} className="text-[#8A8478] hover:text-red-400"><X size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-3 items-center">
          <label className="text-xs text-[#CDC7BA] flex items-center gap-2">
            Status
            <select className="sv-select text-xs" value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="qa-status">
              <option value="draft">Draft (private)</option>
              <option value="pending">Pending Review</option>
              <option value="verified">Verified (publish live)</option>
            </select>
          </label>
          <label className="text-xs text-[#CDC7BA] flex items-center gap-2">
            <input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on Home
          </label>
        </div>
        <button onClick={save} disabled={busy} data-testid="qa-save" className="sv-btn-primary">
          {busy ? "Saving…" : "Save & Publish Model"}
        </button>
      </div>
    </div>
  );
}
